// The COACH's billing notice on the home dashboard (#4393 Surface B, merged with
// #4455), as one unit: its state, when it shows, what it says, what happens when
// it is dismissed or tapped, and the roster watcher it owns.
//
// Moved out of CsDayItemsComponent by #5236, unchanged. The rule about WHETHER to
// show it and WHICH wording to use does not live here — that is
// `decideCoachBillingCard` in cs-day-items-coach-cards.ts, which is pure and
// separately tested, and which asks Phase 1's `resolveCoachLicenseMirror` and the
// shared wording rule. This file gathers that rule's inputs, formats its answer
// for the template, reads the roster when — and only when — the answer needs it,
// and owns the dismissal and the action.
//
// Angular-free on purpose — see cs-day-items-notice-host.ts for why.

import { DateTime } from 'luxon';
import { formatDateMed } from '@cf-shared/date-format';
import { resolveSubscriptionEndInstants } from '@cf-shared/data-model/subscription-end-instants';
import { describeError } from '@cf-shared/error-describe';
import type { CoachPaymentNoticeKind } from '@cf-shared/data-model/coach-billing-notice';
import {
  CoachBillingCardInput,
  decideCoachBillingCard,
  isCoachBillingCardDismissed,
  markCoachBillingCardDismissed,
} from './cs-day-items-coach-cards';
import { DayItemsNoticeController, DayItemsNoticeHost, markNoticeHostForCheck } from './cs-day-items-notice-host';

/**
 * The one thing this notice needs a watcher for: the coach's own roster, read
 * through whatever watcher the host creates for it.
 *
 * Narrow on purpose. Handing the notice the database service would pull
 * `@angular/core` in through it and lose the mocha unit-test path, and it would
 * also let the notice read documents that are none of its business.
 */
export interface CoachRosterReader {
  /**
   * Reads the coach's roster and answers how many clients it lists.
   *
   * The host creates the watcher on the first call and re-runs `refresh` when the
   * document later changes; see `dispose` for the other half of that.
   *
   * Throws when the database cannot be read at all — the caller reports it and
   * counts zero rather than hiding the card.
   */
  countClients(onRosterChanged: () => void): number;
  /** Releases the watcher the host created, if it created one. */
  release(): void;
}

export interface CoachBillingNoticeDeps {
  host: DayItemsNoticeHost;
  /**
   * True while a coach is managing one of their clients. The screen then shows
   * the CLIENT's data over the CLIENT's database, so the coach's own warning must
   * not render there.
   */
  isCoachManaging(): boolean;
  /**
   * The coach's own login result. `CouchLoginResult` satisfies
   * `CoachBillingCardInput['coach']` structurally, so the component passes it
   * straight in.
   */
  getCoach(): CoachBillingCardInput['coach'];
  /** The signed-in person's uid, which keys the per-session dismissal. */
  getUid(): string | undefined;
  /** True for a Paddle per-seat coach, who can fix this inside the app. */
  canManageInApp(): boolean;
  /** Evaluation time, supplied so this stays testable without faking the clock. */
  now(): DateTime;
  /** The locale the date is formatted in. */
  locale: string;
  /** Reads the roster, and owns the watcher that re-runs the decision. */
  roster: CoachRosterReader;
  /** Reports a problem that the user is not interrupted about. */
  trackSilentProblem(name: string, context: Record<string, unknown>): void;
  /** Sends the coach to the page where they can fix their coaching billing. */
  openCoachBillingPage(): void;
}

/**
 * The coach's billing card.
 *
 * **The client count does not gate the card**, and that is the thing not to "tidy
 * up". It only decides whether the card adds a sentence about the coach's
 * clients. A coach with no clients still gets the card, because seat #1 of a
 * Paddle coaching subscription is what pays for their OWN Premium membership — a
 * failed payment costs them their own access.
 */
export class CsCoachBillingNotice implements DayItemsNoticeController {
  /** Whether this notice has something to say. Read by `decideVisibleNotices`. */
  wantsToShow = false;
  /**
   * Which of the three wordings to draw. Only the Paddle rail may name a failed
   * payment — see coach-billing-notice.ts.
   */
  kind: CoachPaymentNoticeKind = 'not-renewing';
  /**
   * The date the plan runs to, formatted for display. Empty when there is none
   * worth showing, and the template then omits that sentence.
   */
  endDate = '';
  /**
   * How many clients lose access because of this. ZERO means the clients sentence
   * is omitted entirely — either the coach has none, the count could not be read,
   * or their clients keep access anyway (an admin-granted Legacy coach). It never
   * hides the card itself.
   */
  clientCount = 0;
  /** True for a Paddle per-seat coach, who can fix this in the app. */
  canManageInApp = false;

  constructor(private deps: CoachBillingNoticeDeps) {}

  /**
   * Re-decides whether the card shows, with which wording, and with what to say
   * about the coach's clients.
   *
   * This is called more than once. The licence half only changes on a fresh
   * sign-in, which re-creates the host component, but the roster half can arrive
   * later — so the roster reader re-runs this when the document turns up.
   */
  refresh(): void {
    // while a coach is managing a client, the database is swapped to the CLIENT's
    // but the login result still describes the coach — so without this the coach's
    // own warning would render on the client's dashboard, over the client's data,
    // and the count would come from whichever roster that swapped database
    // happens to hold.
    if (this.deps.isCoachManaging()) {
      this.hide();
      return;
    }

    const decision = decideCoachBillingCard({
      coach: this.deps.getCoach(),
      now: this.deps.now(),
      dismissedThisSession: isCoachBillingCardDismissed(this.deps.getUid()),
    });

    if (!decision.show) {
      this.hide();
      return;
    }

    this.kind = decision.kind!;
    // the date the user acts on must be their OWN day: the stored expiry is UTC,
    // so a coach at UTC-7 whose block ends 02:00Z would otherwise be told the
    // following date. Names and order then follow the UI language — see
    // design-docs/date-time-localization.md.
    // "your access ends on <date>" / "you keep access until <date>" both ask when
    // OUR ACCESS ends, so this is the access answer — the stored value as
    // written. Routed through the shared helper rather than parsed here so this
    // site cannot drift from the rest (#4278); the rail is left unknown because
    // the access answer does not depend on it, which also suits a decision that
    // may have come from either coach block.
    const { accessEndsAt } = resolveSubscriptionEndInstants(undefined, decision.endDate, {});
    this.endDate = accessEndsAt ? formatDateMed(accessEndsAt.toLocal(), this.deps.locale) : '';
    // Read the roster ONLY now — after the card is known to render AND to have
    // something to say about clients. Reading it registers a DocWatcher, so asking
    // earlier would put one on every user's home screen, coach or not, and would
    // fire the roster-read problem event for the whole user base during a database
    // outage rather than for the few coaches with a card.
    this.clientCount = decision.clientsLoseAccess ? this.countClients() : 0;
    this.canManageInApp = this.deps.canManageInApp();
    this.wantsToShow = true;
    markNoticeHostForCheck(this.deps.host);
  }

  /**
   * Dismissal is per SESSION only — see `dismissedByUid` in the card module for
   * why. It must not suppress the card for the rest of the grace window.
   */
  onDismissed(): void {
    markCoachBillingCardDismissed(this.deps.getUid());
    this.hide();
  }

  /**
   * Only offered to a coach who can actually act on it in the app — a Paddle
   * per-seat coach. A store fixed-tier coach has to do it in their store, so they
   * are told that instead of given a button that cannot help them.
   */
  onActionClick(): void {
    this.deps.openCoachBillingPage();
  }

  /** Releases the roster watcher, if one was ever created. */
  dispose(): void {
    this.deps.roster.release();
  }

  /**
   * How many clients the coach's own roster lists.
   *
   * Watched, not read once. The roster can change while the app runs: a revision
   * synced in from another device reaches the in-memory copy and wakes the watcher.
   * Without a watcher the card would keep the count read at view init for the whole
   * session. (Until #6248 this also covered a boot read that fell back to an empty
   * default; start-up no longer carries on with such a default.)
   */
  private countClients(): number {
    try {
      return this.deps.roster.countClients(() => this.refresh());
    } catch (err) {
      // the singleton is registered when the database initialises, so a throw here
      // means the database is not usable at all. Report it, and count zero:
      // without a roster the card cannot substantiate a claim about anyone's
      // clients, so it says nothing about them — but it still renders, because the
      // coach's own money problem is real either way.
      // describeError, not String(err): a PouchDB error is a
      // { status, error, reason } object, which String() flattens to
      // "[object Object]" — and this crosses the wire as a telemetry property.
      this.deps.trackSilentProblem('CoachBillingCardRosterReadFailed', {
        error: describeError(err),
      });
      return 0;
    }
  }

  private hide(): void {
    this.wantsToShow = false;
    markNoticeHostForCheck(this.deps.host);
  }
}
